import React, { useEffect, useMemo, useState } from 'react'
import { Route, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import {
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ComputerDesktopIcon,
  UsersIcon,
  Squares2X2Icon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline'

import { get, getToken, logout } from './util'
import Landing from './Landing'
import User from './User'
import Device from './Device'
import Config from './Config'
import Profile from './Profile'

const navItems = [
  { key: 'overview', label: 'Overview', icon: Squares2X2Icon },
  { key: 'users', label: 'Users', icon: UsersIcon },
  { key: 'devices', label: 'Devices', icon: ComputerDesktopIcon },
  { key: 'unified-profile', label: 'Unified Profile', icon: AdjustmentsHorizontalIcon, requiresProfile: true }
]

const AppShell = () => {
  const history = useHistory()
  const location = useLocation()
  const match = useRouteMatch()
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [nav, setNav] = useState('overview')
  const [showProfile, setShowProfile] = useState(false)

  useEffect(() => {
    if (!getToken()) {
      history.replace('/login')
      return
    }

    get('admin/info')
      .then(data => setCompany(data))
      .finally(() => setLoading(false))
  }, [history])

  useEffect(() => {
    const relativePath = location.pathname.replace(match.url, '')
    const segments = relativePath.split('/').filter(Boolean)
    setNav(segments[0] || 'overview')
  }, [location.pathname, match.url])

  const availableNavItems = useMemo(() => {
    if (!company) return navItems.filter(item => !item.requiresProfile)
    return navItems.filter(item => (!item.requiresProfile || company.allowsChangingProfile))
  }, [company])

  const handleNavigate = value => {
    if (value === 'overview') {
      history.push(match.url)
    } else {
      history.push(`${match.url}/${value}`)
    }
  }

  if (loading || !company) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-surface text-slate-600'>
        Loading dashboard…
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-surface'>
      <header className='sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur'>
        <div className='max-w-6xl mx-auto flex items-center justify-between px-4 md:px-6 py-3'>
          <div className='leading-tight'>
            <p className='text-lg font-semibold tracking-tight text-ink'>{company.companyName}</p>
            <p className='text-sm text-slate-500'>Company ID · {company.companyID}</p>
          </div>
          <nav className='hidden md:flex items-center gap-4'>
            {availableNavItems.map(item => {
              const Icon = item.icon
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavigate(item.key)}
                  className={`inline-flex items-center gap-2 border-b-2 pb-1 text-sm font-semibold transition ${
                    nav === item.key
                      ? 'border-ink text-ink'
                      : 'border-transparent text-slate-500 hover:text-ink'
                  }`}
                >
                  <Icon className='w-4 h-4' />
                  {item.label}
                </button>
              )
            })}
          </nav>
          <div className='flex items-center gap-2'>
            <button
              className='p-2 rounded-xl border border-slate-200 hover:border-slate-300'
              onClick={() => setShowProfile(true)}
            >
              <UserCircleIcon className='w-5 h-5 text-slate-600' />
            </button>
            <button
              className='p-2 rounded-xl border border-slate-200 hover:border-slate-300'
              onClick={() => {
                logout()
                history.push('/login')
              }}
            >
              <ArrowRightOnRectangleIcon className='w-5 h-5 text-slate-600' />
            </button>
          </div>
        </div>
      </header>

      <main className='max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-6'>
        <Route
          exact
          path={match.path}
          render={routeProps => <Landing {...routeProps} company={company} />}
        />
        <Route
          path={`${match.path}/users`}
          render={() => <User company={company} />}
        />
        <Route path={`${match.path}/devices`} component={Device} />
        <Route path={`${match.path}/unified-profile`} component={Config} />
      </main>

      <Profile
        history={history}
        show={showProfile}
        onClose={() => setShowProfile(false)}
        onChangeDefaultQuota={defaultQuotaPerUser => {
          setCompany(prev => ({ ...prev, defaultQuotaPerUser }))
          setShowProfile(false)
        }}
      />
    </div>
  )
}

export default AppShell
